import {
  QButton,
  useCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';
import { AxiosError } from 'axios';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { documentApi } from '../../../../../../api/document';
import {
  DocumentStatus,
  DocumentTrainee,
  DocumentVersion,
} from '../../../../../../api/model/document';
import { Group } from '../../../../../../api/model/group';
import { Tag } from '../../../../../../api/model/tag';
import { MedtechUserV2 } from '../../../../../../api/user';
import { DocumentOverviewContext } from '../../../Context';
import {
  isDocumentLatestMajorVersion,
  shouldShowManageTraineesButton,
} from '../../../RenderLogic';
import {
  canUserAccessAtLeastOnePrivateTag,
  getDocumentTagIdsToGroupIdsMap,
} from '../../utils';
import { TraineesLookup } from '../TraineesLookup/index';

type ManageTraineesButtonProps = {
  versions?: DocumentVersion[];
  groups: Group[];
  users: MedtechUserV2[];
  tags: Tag[];
};
export const ManageTraineesButton = ({
  versions,
  groups,
  users,
  tags,
}: ManageTraineesButtonProps) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { showToast } = useToastProvider();
  const { qualioDocument, refetchDocumentOverviewData } = useContext(
    DocumentOverviewContext,
  );

  const [isLookupModalOpen, setIsLookupModalOpen] = useState(false);
  const closeLookup = useCallback(() => {
    setIsLookupModalOpen(false);
  }, []);
  const canManageTrainees = shouldShowManageTraineesButton(
    qualioDocument,
    currentUser,
    versions,
  );
  const isLatestMajorVersion = isDocumentLatestMajorVersion(
    qualioDocument,
    versions ?? [],
  );

  const currentTraineesMap = useMemo(
    () =>
      qualioDocument.trainees.reduce(
        (acc: Map<DocumentTrainee['id'], DocumentTrainee>, trainee) => {
          if (!trainee.training_removed) {
            acc.set(trainee.id, trainee);
          }
          return acc;
        },
        new Map(),
      ),
    [qualioDocument.trainees],
  );

  const documentTagIdsToGroupIdsMap = useMemo(
    () => getDocumentTagIdsToGroupIdsMap(qualioDocument, tags),
    [qualioDocument, tags],
  );

  const usersWithoutAccess = useMemo(
    () =>
      users.filter(
        (user) =>
          documentTagIdsToGroupIdsMap.size !== 0 &&
          !canUserAccessAtLeastOnePrivateTag(
            user,
            Array.from(documentTagIdsToGroupIdsMap.values()),
          ),
      ),
    [documentTagIdsToGroupIdsMap, users],
  );

  const visibleUsers = useMemo(
    () =>
      users.filter(
        (user) =>
          documentTagIdsToGroupIdsMap.size === 0 ||
          currentTraineesMap.has(user.id) ||
          canUserAccessAtLeastOnePrivateTag(
            user,
            Array.from(documentTagIdsToGroupIdsMap.values()),
          ),
      ),
    [users, currentTraineesMap, documentTagIdsToGroupIdsMap],
  );

  const isTraineeRowPreselected = (user: MedtechUserV2) =>
    currentTraineesMap.has(user.id);

  const isTraineeRowDisabled = (user: MedtechUserV2) => {
    const currentTrainee = currentTraineesMap.get(user.id);
    return Boolean(currentTrainee?.training_plan);
  };

  const updateTrainees = async (selections: readonly MedtechUserV2[]) => {
    const commonToastProps = {
      id: 'manage-trainees-overview-toast',
      replace: true,
    };
    try {
      await documentApi.updateDocumentTrainees(
        qualioDocument.id,
        selections.map(({ id }) => id),
        !isLatestMajorVersion &&
          qualioDocument.status_id === DocumentStatus.Effective.valueOf(),
      );
      showToast({
        'data-cy': 'manage-trainees-button-notify-success-toast',
        ...commonToastProps,
        status: 'success',
        title: t('notifications.traineesAssigned'),
        description: t('notifications.successfullyUpdatedTrainees'),
      });
      refetchDocumentOverviewData();
      closeLookup();
    } catch (error) {
      const message =
        error instanceof AxiosError
          ? t('errors.failedToApplyChangesToTrainees', {
              error: `${error.response?.data.message}.`,
            })
          : t('errors.failedToApplyChangesToTrainees', {
              error: 'Please try again.',
            });

      showToast({
        'data-cy': 'manage-trainees-button-notify-error-toast',
        ...commonToastProps,
        status: 'error',
        title: t('notifications.error'),
        description: message,
      });
      // Re-throw error so that QLookup does not reset the selections
      throw error;
    }
  };

  if (!canManageTrainees) {
    return null;
  }

  return (
    <>
      <QButton
        data-cy="document-manage-trainees-button"
        variant="ghost"
        onClick={() => setIsLookupModalOpen(true)}
      >
        {t('buttons.manage')}
      </QButton>
      <TraineesLookup
        title={t('buttons.manageTrainees')}
        subtitle={t('messages.assignUsersWhoNeedTraining')}
        isOpen={isLookupModalOpen}
        onClose={closeLookup}
        trainees={visibleUsers}
        usersWithoutAccess={usersWithoutAccess}
        groups={groups}
        updateTrainees={updateTrainees}
        isTraineePreselected={isTraineeRowPreselected}
        isTraineeDisabled={isTraineeRowDisabled}
      />
    </>
  );
};
